import { PaymentMethodTypes, ccTypeMap } from '@/feature/deposit/const';
import { ClosedLoopMethodProps } from '@/feature/withdraw/components/closedLoop.types';
import { WithdrawStackParamList } from '@/navigation/types';
import { toLocaleCurrency } from '@/utils/numeric/currency';
import { roundToTwoDecimals } from '@/utils/round-two-decimals';

import { WithdrawMethod, WithdrawMethodTypes } from '../hooks/use-withdrawal-methods';

export const paymentMethodLabels: Record<string, string> = {
    paysafe: 'Debit card',
    paysafe_paypal: 'Complete on PayPal',
    paysafe_mazooma: 'Account',
    paysafe_venmo: 'Venmo',
    trustly: 'Account via Trustly',
};

export const WITHDRAWAL_LIMITS = {
    maxSweepsWithdrawalLimit: '5000',
    maxWithdrawalLimit: '50000',
    minWithdrawalLimit: '5',
};

export const LOG_TAG = '[Withdrawal]';
export const LOG_TAG_MULTIPLE = '[Multiple Withdrawal]';

export type WithdrawHomeRoute =
    | { screen: 'ChooseWithdrawalMethod' }
    | { screen: 'WithdrawForm'; params: WithdrawStackParamList['WithdrawForm'] };

/**
 * Destination when entering the withdraw flow: straight to the form when there is
 * exactly one method to select, otherwise the method chooser — which fetches its own
 * data, so it also covers an empty or unknown list.
 */
export const getWithdrawHomeRoute = (
    methods: WithdrawMethod[],
    isFantasyMoneyRedemptionEnabled: boolean
): WithdrawHomeRoute =>
    methods.length !== 1 || isFantasyMoneyRedemptionEnabled
        ? { screen: 'ChooseWithdrawalMethod' }
        : { screen: 'WithdrawForm', params: { selectedMethod: methods[0], methods } };

export type FilterWithdrawableMethodsFlags = {
    isVerifiedWithRestrictedBalance: boolean;
    needsVerificationByLightSdd: boolean;
};

/**
 * Filters withdrawal methods for entering the withdraw flow:
 * 1. Drops site-id methods whose closed loop is already completed.
 * 2. Sorts expired methods to the bottom.
 * 3. Unless the user is verified with a restricted balance, restricts to methods
 *    with an incomplete closed loop when any exist.
 *
 * Method types are deliberately not filtered here: rails retired for new deposits
 * (IBT, Pay By Bank) must stay withdrawable for users who attached them before,
 * since closed-loop returns may have to go back to them. New attachments are
 * prevented on the deposit side instead.
 */
export const filterWithdrawableMethods = (
    methods: WithdrawMethod[],
    { isVerifiedWithRestrictedBalance, needsVerificationByLightSdd }: FilterWithdrawableMethodsFlags
): WithdrawMethod[] => {
    const availableWithdrawalMethods = methods.filter(
        method => !method.via_site_id || (method.via_site_id && !method.closed_loop_completed)
    );
    const sortedWithdrawMethods = availableWithdrawalMethods.sort(a => (a.is_expired ? 1 : -1));
    const closedLoopNotCompletedList = sortedWithdrawMethods.filter(method => !method?.closed_loop_completed);

    return isVerifiedWithRestrictedBalance && !needsVerificationByLightSdd
        ? sortedWithdrawMethods
        : closedLoopNotCompletedList.length > 0
        ? closedLoopNotCompletedList
        : sortedWithdrawMethods;
};

// Returns the most recently linked Trustly method, falling back to the first available
// method if none is found. Returns `undefined` only when `methods` is empty.
export const getLatestTrustlyMethod = (methods: WithdrawMethod[]): WithdrawMethod | undefined => {
    const latestTrustly = methods
        .filter(method => method.type === PaymentMethodTypes.Trustly)
        .sort((a, b) => (b.closed_loop_id ?? 0) - (a.closed_loop_id ?? 0))[0];
    return latestTrustly ?? methods[0];
};

/**
 * Filters withdrawal methods based on closed-loop required distribution.
 *
 * - Trustly verified / light SDD users (`isTrustlyVerified`): all methods are shown.
 * - Otherwise only methods that still have a required distribution
 *   (`closed_loop_remain` > 0) are shown.
 * - If no method has a required distribution (every `closed_loop_remain` is 0),
 *   all methods are shown.
 */
export const filterMethodsByRequiredDistribution = (
    methods: WithdrawMethod[],
    isTrustlyVerified: boolean
): WithdrawMethod[] => {
    if (isTrustlyVerified) {
        return methods;
    }
    const methodsWithRequiredDistribution = methods.filter(method => method.closed_loop_remain > 0);
    return methodsWithRequiredDistribution.length > 0 ? methodsWithRequiredDistribution : methods;
};

/**
 * Get withdrawal method name for withdrawal method list (PayPal, Debit card, Account name, etc.)
 */
export const getWithdrawalMethodName = (methodType: WithdrawMethodTypes, cc_type: string = '') => {
    switch (methodType) {
        case PaymentMethodTypes.PaysafePaypal:
            return 'PayPal';
        case PaymentMethodTypes.PaysafeMazooma:
        case PaymentMethodTypes.PaysafePayByBank:
        case PaymentMethodTypes.Trustly:
            return `${cc_type}`;
        case PaymentMethodTypes.PaysafeVenmo:
            return 'Venmo';
        default:
            return `${cc_type ? ccTypeMap[cc_type] : 'Debit card'}`;
    }
};

/**
 * Function to add a new distribution item to the list of closed-loop
 * withdrawal methods and its distribution amount.
 * @param distributionArray - List (array) of closed-loop withdrawal methods
 * @param method - Withdrawal method
 * @param amountToReturn - Amount to withdraw to each method (distribution value)
 */
export const addDistribution = (
    distributionArray: ClosedLoopMethodProps[],
    method: WithdrawMethod,
    amountToReturn: number
) => {
    const distributionObject = {
        ...method,
        amountToReturn,
    };
    const newDistributionObject = [...distributionArray, distributionObject];
    return newDistributionObject;
};

/**
 * Function used to get the closed-loop methods array with the withdrawal distribution
 * for each withdrawal method.
 */
export const getDistribution = (
    methods: WithdrawMethod[],
    selectedMethod: WithdrawMethod,
    amountToWithdraw: number
): ClosedLoopMethodProps[] => {
    /* Getting the methods list without the selected method. */
    const filteredMethods = methods.filter(method => selectedMethod.payment_type_id !== method.payment_type_id);
    if (!filteredMethods.length) {
        return [];
    }
    /* Sorting filtered methods list in descending order based on closed_loop_remain value */
    const methodsDesc = filteredMethods.sort((a, b) => b.closed_loop_remain - a.closed_loop_remain);
    let closedLoopMethodsArray: ClosedLoopMethodProps[] = [];
    let amountToWithdrawRemaining = amountToWithdraw;

    /* Get the required amount to return to the selected method */
    let selectedMethodAmountToReturn = selectedMethod.closed_loop_remain;

    /* subtracting the closed loop remain of the selected method from the total amount to withdraw */
    amountToWithdrawRemaining = roundToTwoDecimals(amountToWithdrawRemaining - selectedMethod.closed_loop_remain);

    /* Adding non-selected methods distribution to closed loop methods list */
    methodsDesc.forEach((method: WithdrawMethod) => {
        if (amountToWithdrawRemaining > 0) {
            const maxAmountToWithdrawByMethod =
                amountToWithdrawRemaining > method?.closed_loop_remain
                    ? method?.closed_loop_remain
                    : amountToWithdrawRemaining;

            closedLoopMethodsArray = addDistribution(closedLoopMethodsArray, method, maxAmountToWithdrawByMethod);

            amountToWithdrawRemaining -= maxAmountToWithdrawByMethod;
        }
    });
    /* Add the remain amount to selected method */
    if (amountToWithdrawRemaining > 0) {
        selectedMethodAmountToReturn += amountToWithdrawRemaining;
    }

    closedLoopMethodsArray = addDistribution(closedLoopMethodsArray, selectedMethod, selectedMethodAmountToReturn);

    return closedLoopMethodsArray;
};

/**
 * Generates an error message based on the withdrawal amount input and various limits.
 *
 * @param maxWithdrawalLimit - The maximum amount that can be withdrawn in a single transaction.
 * @param amountInput - The input amount as a string.
 * @param balance - The current balance available for withdrawal.
 * @param isPicksActive - Optional flag indicating if picks are active.
 * @param minWithdrawalLimit - Optional minimum amount that can be withdrawn.
 * @returns A string containing the appropriate error message, or an empty string if no errors are found.
 */
export const getErrorMessage = (
    maxWithdrawalLimit: number,
    amountInput: string,
    balance: number,
    showMinError?: boolean,
    minWithdrawalLimit?: number,
    isSharedSocialWallet?: boolean
): string => {
    const validateArray = amountInput.split('.');
    if (validateArray.length > 2 || validateArray[0] === '' || validateArray[1] === '' || amountInput.includes(',')) {
        return 'Please enter a valid amount.';
    }

    const withdrawText = isSharedSocialWallet ? 'redemption' : 'withdraw';
    const withdrawalLimitText = isSharedSocialWallet ? 'redemption' : 'withdrawable';

    const amount = parseFloat(amountInput);
    const displayMinimumWithdrawError =
        minWithdrawalLimit && amount < minWithdrawalLimit && amount !== 0 && showMinError;

    if (amount > maxWithdrawalLimit) {
        return `Maximum single ${withdrawText} amount: ${toLocaleCurrency(maxWithdrawalLimit)}`;
    } else if (amount > balance) {
        return `Maximum ${withdrawalLimitText} amount: ${toLocaleCurrency(balance)}`;
    } else if (displayMinimumWithdrawError) {
        return `Minimum ${withdrawalLimitText} amount: ${toLocaleCurrency(minWithdrawalLimit)}`;
    } else {
        return '';
    }
};

/**
 * Formats the withdrawal method display text with additional details
 */
export const formatWithdrawalMethodDisplay = (
    methodType: string,
    ccLast4?: string | null,
    venmoId?: string | null
): string => {
    const baseLabel = paymentMethodLabels[methodType];
    const cardSuffix = ccLast4 ? `(•••• ${ccLast4})` : '';
    const venmoSuffix = venmoId ? `(${venmoId})` : '';

    return `${baseLabel} ${cardSuffix}${venmoSuffix}`.trim();
};
